/**
 * ComparisonScoring — column-per-site overall score + per-criterion
 * weighted-score breakdown with delta arrows showing each site's
 * deviation from the top-ranked site. Optional radar overlay toggles
 * a Chart.js radar at the bottom of the panel.
 *
 *   ┌────────────────┬──────────┬────────────┬────────────┐
 *   │                │ Site A   │ Site B     │ Site C     │
 *   │                │ #1       │ #2         │ #3         │  ← rank pill
 *   ├────────────────┼──────────┼────────────┼────────────┤
 *   │ Overall score  │ 8.4      │ 7.9 ▼ −0.5 │ 7.1 ▼ −1.3 │
 *   │ Location       │ 7.5      │ 8.0 ▲ +0.5 │ 6.0 ▼ −1.5 │
 *   │ Demographics   │ 9.0      │ 7.5 ▼ −1.5 │ 8.0 ▼ −1.0 │
 *   │ Cost           │ 8.0      │ 7.0 ▼ −1.0 │ 7.5 ▼ −0.5 │
 *   └────────────────┴──────────┴────────────┴────────────┘
 *
 * Delta is the *displayed* score minus the top-ranked site's score for
 * that row. The leader's column shows raw values without an arrow; the
 * other columns show the delta with an up/down/equal arrow.
 *
 * The chart uses the same Chart.js radar setup as the workspace
 * VisualizationPanel for consistency. Issue body suggested Recharts but
 * the rest of the scoring suite uses Chart.js, so we stay aligned.
 *
 * Sort: columns are reordered by overall ranking, top first. The
 * picker still controls *which* sites appear; F3 just sorts them.
 *
 * Linear: MYD-218 (F3).
 */

import { useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import {
	Chart as ChartJS,
	RadialLinearScale,
	PointElement,
	LineElement,
	Filler,
	CategoryScale,
	LinearScale,
	Tooltip,
	Legend,
} from 'chart.js'
import { Radar } from 'react-chartjs-2'
import { Card, CardContent, CardHeader } from 'components/ui/card'
import { Button } from 'components/ui/button'
import { Activity, ArrowUp, ArrowDown, Minus, BarChart3 } from 'lucide-react'
import { cn } from 'lib/utils'

import ComparisonRowLabel from './ComparisonRowLabel'
import { gridTemplate } from './comparisonShared'

ChartJS.register(
	RadialLinearScale,
	PointElement,
	LineElement,
	Filler,
	CategoryScale,
	LinearScale,
	Tooltip,
	Legend,
)

const SITE_COLORS = [
	'#0080FE',
	'#22C55E',
	'#F97316',
	'#A855F7',
	'#EC4899',
	'#14B8A6',
	'#EAB308',
	'#EF4444',
]

interface ComparisonScoringProps {
	evalId: string
	sites: any[]
}

interface RankedSite {
	site: any
	siteId: string
	overallScore: number | null
	criteriaByName: Record<string, number | null>
	rank: number
}

const ComparisonScoring = ({ evalId, sites }: ComparisonScoringProps) => {
	const summary = useSelector((state: any) => state.scoring?.summaryByEvaluation?.[evalId])
	const [showRadar, setShowRadar] = useState(true)

	// Index summary by siteId for quick lookup, then order the picker's
	// sites by their overall rank (highest first; null scores tail).
	const summaryBySiteId: Record<string, any> = useMemo(() => {
		const map: Record<string, any> = {}
		for (const s of summary?.sites ?? []) map[String(s.siteId)] = s
		return map
	}, [summary])

	const rankedSites: RankedSite[] = useMemo(() => {
		const rows = sites.map((site) => {
			const s = summaryBySiteId[site._id]
			const criteriaByName: Record<string, number | null> = {}
			for (const c of s?.criteria ?? []) {
				criteriaByName[c.name] = c.avgScore ?? null
			}
			return {
				site,
				siteId: site._id,
				overallScore: s?.overallScore ?? null,
				criteriaByName,
				rank: 0,
			}
		})
		rows.sort((a, b) => {
			const aS = a.overallScore ?? -Infinity
			const bS = b.overallScore ?? -Infinity
			if (aS !== bS) return bS - aS
			return String(a.site.name).localeCompare(String(b.site.name))
		})
		rows.forEach((row, idx) => {
			row.rank = idx + 1
		})
		return rows
	}, [sites, summaryBySiteId])

	// Union of criterion names across selected sites, ordered by the leader's
	// criteria order so the table reads top-down by importance.
	const criterionNames: string[] = useMemo(() => {
		const seen = new Set<string>()
		const ordered: string[] = []
		const leader = summaryBySiteId[rankedSites[0]?.siteId]
		const leaderCriteria = leader?.criteria ?? []
		for (const c of leaderCriteria) {
			if (!seen.has(c.name)) {
				seen.add(c.name)
				ordered.push(c.name)
			}
		}
		// Also fold in any criteria the leader doesn't have but other sites do.
		for (const row of rankedSites) {
			for (const name of Object.keys(row.criteriaByName)) {
				if (!seen.has(name)) {
					seen.add(name)
					ordered.push(name)
				}
			}
		}
		return ordered
	}, [rankedSites, summaryBySiteId])

	const leader = rankedSites[0]
	const hasScores = rankedSites.some((r) => r.overallScore != null)

	const renderDelta = (value: number | null, leaderValue: number | null) => {
		if (value == null) {
			return <span className="text-gray-400">—</span>
		}
		if (leaderValue == null || leader?.siteId === undefined) {
			return <span className="font-semibold text-gray-900">{value.toFixed(1)}</span>
		}
		const delta = value - leaderValue
		const sign = Math.abs(delta) < 0.05 ? '0' : delta > 0 ? '+' : '-'
		const Icon = sign === '0' ? Minus : sign === '+' ? ArrowUp : ArrowDown
		const colorClass =
			sign === '0' ? 'text-gray-400' : sign === '+' ? 'text-green-600' : 'text-red-500'
		return (
			<span className="inline-flex items-center gap-1.5">
				<span className="font-semibold text-gray-900">{value.toFixed(1)}</span>
				<Icon className={cn('w-3 h-3', colorClass)} />
				<span className={cn('text-[11px] font-medium', colorClass)}>
					{sign === '0' ? '0' : `${delta > 0 ? '+' : ''}${delta.toFixed(1)}`}
				</span>
			</span>
		)
	}

	const radarData = useMemo(() => {
		if (criterionNames.length === 0) return null
		return {
			labels: criterionNames,
			datasets: rankedSites.map((row, idx) => {
				const color = SITE_COLORS[idx % SITE_COLORS.length]
				return {
					label: row.site.name,
					data: criterionNames.map((name) => row.criteriaByName[name] ?? 0),
					backgroundColor: `${color}26`,
					borderColor: color,
					borderWidth: 2,
					pointRadius: 2,
					pointBackgroundColor: color,
					pointBorderColor: '#fff',
				}
			}),
		}
	}, [rankedSites, criterionNames])

	const radarOptions = useMemo(
		() => ({
			responsive: true,
			maintainAspectRatio: false,
			scales: {
				r: {
					min: 0,
					max: 10,
					ticks: {
						stepSize: 2,
						color: '#9ca3af',
						backdropColor: 'transparent' as const,
						font: { size: 10 },
					},
					grid: { color: '#e5e7eb' },
					angleLines: { color: '#e5e7eb' },
					pointLabels: { color: '#374151', font: { size: 11 } },
				},
			},
			plugins: {
				legend: {
					position: 'bottom' as const,
					labels: { color: '#6b7280', font: { size: 11 }, boxWidth: 12 },
				},
				tooltip: {
					callbacks: {
						label: (ctx: any) => `${ctx.dataset.label}: ${Number(ctx.parsed.r).toFixed(1)}/10`,
					},
				},
			},
		}),
		[],
	)

	if (!hasScores) {
		return (
			<Card className="p-8 text-center">
				<Activity className="w-8 h-8 mx-auto text-gray-300 mb-3" />
				<h4 className="text-sm font-semibold text-gray-700 mb-1">No scoring data yet</h4>
				<p className="text-xs text-gray-500">
					Score sites in the Scoring tab to populate this comparison.
				</p>
			</Card>
		)
	}

	return (
		<div className="space-y-4">
			{/* Score grid */}
			<div className="overflow-x-auto rounded-md border border-gray-200 bg-white">
				<div
					className="grid"
					style={{
						gridTemplateColumns: gridTemplate(rankedSites.length),
						minWidth: '100%',
					}}
				>
					{/* Header row */}
					<div className="bg-gray-50 border-b border-gray-200 px-3 py-2 sticky left-0 z-10" />
					{rankedSites.map((row) => (
						<div
							key={row.siteId}
							className="bg-gray-50 border-b border-gray-200 border-l first:border-l-0 px-3 py-2.5"
						>
							<h4
								className="text-sm font-semibold text-gray-900 truncate"
								title={row.site.name}
							>
								{row.site.name}
							</h4>
							<span
								className={cn(
									'inline-block mt-0.5 text-[10px] uppercase tracking-wide font-medium px-1.5 py-0.5 rounded',
									row.rank === 1
										? 'bg-[#0080fe] text-white'
										: 'bg-gray-200 text-gray-700',
								)}
							>
								#{row.rank}
							</span>
						</div>
					))}

					{/* Overall score row */}
					<ComparisonRowLabel label="Overall score" className="bg-blue-50/40" />
					{rankedSites.map((row, idx) => (
						<div
							key={row.siteId}
							className="px-3 py-2.5 text-sm border-t border-gray-100 bg-blue-50/40"
						>
							{idx === 0 ? (
								<span className="font-semibold text-[#0080fe] text-base">
									{row.overallScore != null ? row.overallScore.toFixed(1) : '—'}
								</span>
							) : (
								renderDelta(row.overallScore, leader?.overallScore ?? null)
							)}
						</div>
					))}

					{/* Per-criterion rows */}
					{criterionNames.map((name, rowIdx) => (
						<div className="contents" key={name}>
							<ComparisonRowLabel
								label={name}
								className={rowIdx % 2 === 0 ? 'bg-white' : 'bg-gray-50/60'}
							/>
							{rankedSites.map((row, idx) => (
								<div
									key={row.siteId}
									className={cn(
										'px-3 py-2 text-sm border-t border-gray-100',
										rowIdx % 2 === 0 ? 'bg-white' : 'bg-gray-50/60',
									)}
								>
									{idx === 0 ? (
										row.criteriaByName[name] != null ? (
											<span className="font-semibold text-gray-900">
												{row.criteriaByName[name]?.toFixed(1)}
											</span>
										) : (
											<span className="text-gray-400">—</span>
										)
									) : (
										renderDelta(
											row.criteriaByName[name] ?? null,
											leader?.criteriaByName[name] ?? null,
										)
									)}
								</div>
							))}
						</div>
					))}
				</div>
			</div>

			{/* Radar overlay toggle */}
			<Card>
				<CardHeader className="pb-3 flex-row items-center justify-between">
					<h4 className="text-sm font-semibold text-gray-700 uppercase tracking-wide flex items-center gap-1.5">
						<BarChart3 className="w-4 h-4" />
						Radar overlay
					</h4>
					<Button
						variant="primary"
						isOutline
						onClick={() => setShowRadar((v) => !v)}
						className="h-7 px-2 text-xs"
					>
						{showRadar ? 'Hide' : 'Show'}
					</Button>
				</CardHeader>
				{showRadar && radarData && (
					<CardContent>
						<div className="h-[420px]">
							<Radar data={radarData} options={radarOptions} />
						</div>
					</CardContent>
				)}
			</Card>
		</div>
	)
}

export default ComparisonScoring
